import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, ImagePlus, X, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOLD = '#A9B2B9';
const POLL_INTERVAL = 10000; // 10 seconds

function AnnouncementsPage({ isAdmin, currentUserId }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // For refresh button animation
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAnn, setCurrentAnn] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', image_url: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedAnn, setSelectedAnn] = useState(null);
  const [newAnnNotif, setNewAnnNotif] = useState(null); // { title }
  const fileInputRef = useRef(null);
  const knownIdsRef = useRef(null); // track known IDs to detect new ones
  const notifTimerRef = useRef(null);

  const fetchAnnouncements = useCallback(async (isInitial = false) => {
    try {
      if (!isInitial) setRefreshing(true); // Show refresh animation
      const response = await axios.get(`${API}/announcements`, { withCredentials: true });
      const data = response.data || [];

      if (isInitial) {
        // On first load, just store IDs silently
        knownIdsRef.current = new Set(data.map(a => a.announcement_id));
        setAnnouncements(data);
        setLoading(false);
        return;
      }

      // Detect new announcements
      if (knownIdsRef.current) {
        const newOnes = data.filter(a => !knownIdsRef.current.has(a.announcement_id));
        if (newOnes.length > 0) {
          // Update known IDs
          knownIdsRef.current = new Set(data.map(a => a.announcement_id));
          // Show notification for the most recent new announcement
          const latest = newOnes[0];
          if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
          setNewAnnNotif({ title: latest.title });
          notifTimerRef.current = setTimeout(() => setNewAnnNotif(null), 5000);
        }
      } else {
        knownIdsRef.current = new Set(data.map(a => a.announcement_id));
      }

      setAnnouncements(data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      if (isInitial) setLoading(false);
    } finally {
      if (!isInitial) {
        // Small delay to show animation
        setTimeout(() => setRefreshing(false), 300);
      }
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements(true);

    const interval = setInterval(() => fetchAnnouncements(false), POLL_INTERVAL);
    return () => {
      clearInterval(interval);
      if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    };
  }, [fetchAnnouncements]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalImageUrl = formData.image_url;

      // Upload image if a new file was selected
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        finalImageUrl = uploadRes.data.url;
      }

      const payload = { ...formData, image_url: finalImageUrl || null };

      if (editMode && currentAnn) {
        await axios.put(`${API}/announcements/${currentAnn.announcement_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/announcements`, payload, { withCredentials: true });
      }

      setDialogOpen(false);
      resetForm();
      fetchAnnouncements(false);
    } catch (error) {
      console.error('Failed to save announcement:', error);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', image_url: '' });
    setImageFile(null);
    setImagePreview('');
    setEditMode(false);
    setCurrentAnn(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (ann) => {
    setCurrentAnn(ann);
    setFormData({ title: ann.title, content: ann.content, image_url: ann.image_url || '' });
    setImagePreview(ann.image_url || '');
    setImageFile(null);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/announcements/${deleteId}`, { withCredentials: true });
      fetchAnnouncements(false);
    } catch (error) {
      console.error('Failed to delete announcement:', error);
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Refresh Button - Fixed Top Right (Smaller) */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => fetchAnnouncements(false)}
          disabled={refreshing}
          className="bg-white/5 hover:bg-white/10 border border-white/10 rounded p-1.5 transition-colors shadow-lg backdrop-blur-sm disabled:opacity-50"
          title="Ανανέωση"
          data-testid="refresh-announcements-btn"
        >
          <svg 
            width="16" 
            height="16" 
            fill="none" 
            stroke="white" 
            strokeWidth="2" 
            viewBox="0 0 24 24"
            className={refreshing ? 'animate-spin' : ''}
          >
            <path d="M23 4v6h-6M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>

      {/* New announcement toast notification */}
      {newAnnNotif && (
        <div
          className="fixed top-5 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-3 px-5 py-3.5 rounded-lg shadow-2xl border"
          style={{ background: '#0f0f0f', borderColor: `${GOLD}60`, minWidth: 300 }}
          data-testid="new-announcement-notif"
        >
          <Bell className="w-4 h-4 shrink-0 animate-bounce" style={{ color: GOLD }} />
          <div className="min-w-0">
            <p className="text-white text-xs font-medium">Νέα Ανακοίνωση</p>
            <p className="text-gray-400 text-xs font-light truncate">{newAnnNotif.title}</p>
          </div>
          <button onClick={() => setNewAnnNotif(null)} className="ml-auto text-gray-600 hover:text-white transition-colors shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Ανακοινώσεις</h2>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                data-testid="create-announcement-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Νέα Ανακοίνωση
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <p className="premium-modal-eyebrow">DIMAKAS Announcements</p>
                <DialogTitle>
                  {editMode ? 'Επεξεργασία Ανακοίνωσης' : 'Νέα Ανακοίνωση'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  placeholder="Τίτλος"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="premium-modal-input border-white/15 text-white placeholder:text-gray-500"
                  data-testid="announcement-title-input"
                />
                <Textarea
                  placeholder="Περιεχόμενο"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={4}
                  className="premium-modal-input min-h-[120px] border-white/15 text-white placeholder:text-gray-500"
                  data-testid="announcement-content-input"
                />

                {/* Image Section */}
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    data-testid="announcement-image-input"
                  />

                  {imagePreview ? (
                    <div className="relative group">
                      <img
                        src={imagePreview.startsWith('/api/') ? `${BACKEND_URL}${imagePreview}` : imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={clearImage}
                        className="absolute top-2 right-2 premium-modal-close"
                        data-testid="remove-image-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-2 right-2 premium-modal-secondary min-h-0 px-3 py-2 text-[11px] font-light tracking-[0.2em]"
                      >
                        Αλλαγή
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border border-dashed border-white/20 rounded-[24px] flex flex-col items-center justify-center space-y-2 text-gray-400 hover:border-[#A9B2B9]/40 hover:text-[#A9B2B9] transition-all"
                      data-testid="upload-image-btn"
                    >
                      <ImagePlus className="w-6 h-6" />
                      <span className="text-sm font-light">Προσθήκη Φωτογραφίας (προαιρετικό)</span>
                    </button>
                  )}
                </div>

                <div className="flex justify-end space-x-2 pt-1">
                  <Button type="button" variant="outline" className="premium-modal-secondary" onClick={() => setDialogOpen(false)}>
                    Ακύρωση
                  </Button>
                  <Button type="submit" className="premium-modal-primary" disabled={uploading} data-testid="save-announcement-btn">
                    {uploading ? 'Αποθήκευση...' : 'Αποθήκευση'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-6 stagger">
        {announcements.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center text-gray-400">
              Δεν υπάρχουν ανακοινώσεις
            </CardContent>
          </Card>
        ) : (
          announcements.map((ann) => (
            <Card 
              key={ann.announcement_id} 
              className="bg-white/5 border-white/10 card-lift overflow-hidden cursor-pointer hover:bg-white/[0.07] transition-colors" 
              data-testid={`announcement-${ann.announcement_id}`} 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'instant' });
                setSelectedAnn(ann);
              }}
            >
              {ann.image_url && (
                <div className="w-full h-56 overflow-hidden">
                  <img
                    src={ann.image_url.startsWith('/api/') ? `${BACKEND_URL}${ann.image_url}` : ann.image_url}
                    alt={ann.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <CardTitle className="text-white text-xl">{ann.title}</CardTitle>
                      {ann.target_user_id && (
                        <span
                          className="text-xs px-2 py-0.5 border font-light tracking-widest"
                          style={{ color: '#A9B2B9', borderColor: '#A9B2B950', background: '#A9B2B910' }}
                          data-testid={`personal-badge-${ann.announcement_id}`}
                        >
                          ΠΡΟΣΩΠΙΚΟ
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-0.5">
                      {(() => {
                        const utcDate = new Date(ann.created_at);
                        return new Intl.DateTimeFormat('el-GR', {
                          timeZone: 'Europe/Athens',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }).format(utcDate);
                      })()}
                    </p>
                  </div>
                  {/* Admin can delete/edit all announcements (public and personal) */}
                  {isAdmin && (
                    <div className="flex space-x-2">
                      {!ann.target_user_id && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleEdit(ann); }} 
                          className="text-white hover:text-gray-300"
                          data-testid={`edit-announcement-${ann.announcement_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleDelete(ann.announcement_id); }} 
                        className="text-red-500 hover:text-red-400"
                        data-testid={`delete-announcement-${ann.announcement_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {/* User can delete their own personal announcements */}
                  {!isAdmin && ann.target_user_id && (
                    <Button
                      variant="ghost" size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(ann.announcement_id); }}
                      className="text-gray-600 hover:text-white"
                      data-testid={`delete-personal-${ann.announcement_id}`}
                      title="Διαγραφή"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 whitespace-pre-wrap">{ann.content || ann.message}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <p className="premium-modal-eyebrow">DIMAKAS Announcements</p>
            <AlertDialogTitle>Διαγραφή Ανακοίνωσης</AlertDialogTitle>
            <AlertDialogDescription>
              Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την ανακοίνωση; Η ενέργεια δεν μπορεί να αναιρεθεί.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Ακύρωση
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="premium-modal-danger"
              data-testid="confirm-delete-btn"
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Announcement Detail Popup */}
      {selectedAnn && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-8 animate-fadeIn overflow-y-auto"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)' }}
          onClick={() => setSelectedAnn(null)}
        >
          <div
            className="premium-modal-panel w-full max-w-lg overflow-hidden max-h-[80vh] overflow-y-auto animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            data-testid="announcement-popup"
          >
            {selectedAnn.image_url && (
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={selectedAnn.image_url.startsWith('/api/') ? `${BACKEND_URL}${selectedAnn.image_url}` : selectedAnn.image_url}
                  alt={selectedAnn.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="p-6 space-y-4">
              <div className="premium-modal-divider" aria-hidden="true">
                <span className="premium-modal-divider-line" />
                <span className="premium-modal-divider-dot" />
                <span className="premium-modal-divider-line" />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="premium-modal-eyebrow mb-2">DIMAKAS Update</p>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h2 className="premium-modal-heading text-xl">{selectedAnn.title}</h2>
                    {selectedAnn.target_user_id && (
                      <span className="text-xs px-2 py-0.5 border font-light tracking-widest" style={{ color: '#A9B2B9', borderColor: '#A9B2B950', background: '#A9B2B910' }}>ΠΡΟΣΩΠΙΚΟ</span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500">
                    {(() => {
                      const utcDate = new Date(selectedAnn.created_at);
                      return new Intl.DateTimeFormat('el-GR', {
                        timeZone: 'Europe/Athens',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }).format(utcDate);
                    })()}
                  </p>
                </div>
                <button onClick={() => setSelectedAnn(null)} className="premium-modal-close flex-shrink-0" data-testid="close-popup-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="premium-modal-copy whitespace-pre-wrap">{selectedAnn.content || selectedAnn.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsPage;
